import classNames from 'classnames'
import { useState, useRef, useEffect } from 'react'
import { useSiteContext, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import SiteLangSwitcher from './header-site-lang-switcher'
import SolutionsMenu from './header-solutions-menu'
import { MobileStateActionType, SubMenu, useHeaderContext } from './header-context'
import { getKnowledgeBankUrl } from '@dustin-web/microsite-config'
import MobileHeaderBreadcrumb from './mobile-header-breadcrumb'
import { CategorySecondLevel, CategoryListButton } from './header-category-tree-menu'
import { stripTrailingSlash } from '@dustin-web/microsite-components/src/utils/urls'
import BrandMenu from './header-brand-menu'
import ScrollContainer from '@dustin-web/microsite-components/src/scroll-container'
import { getMainCategoryIcon } from '@dustin-web/microsite-components/src/utils/main-category-icons'
import {
  NavigationEventType,
  NavigationSelectionType,
} from '@dustin-web/microsite-components/src/utils/digital-data-types'

// Types
import { GetCategoryNavigationQuery } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'

// GraphQl
import { GET_CATEGORY_NAVIGATION } from '@dustin-web/microsite-data/src/graphql/product/category-navigation.graphql'
import { DigitalData } from '@dustin-web/microsite-components/src/utils/digital-data'

const TOP_CATEGORY_ID = '2037738'

const getCategoryType = (categoryId: string) =>
  categoryId.includes('StructureGroup_')
    ? NavigationSelectionType.SoftCategory
    : NavigationSelectionType.Category

const MobileHeaderMenuABTest = () => {
  const [activeCategory, setActiveCategory] = useState(TOP_CATEGORY_ID)
  const [accordionsRefreshKey, setAccordionsRefreshKey] = useState<number>(0)
  const { mobile, dispatchMobile } = useHeaderContext()
  const { translate } = useTranslations()
  const { sourcingCompany } = useSiteContext()
  const [showMobileBrands, setShowMobileBrands] = useState(false)
  const [showMobileSolutions, setShowMobileSolutions] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const firstRender = useRef<boolean>(true)
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    DigitalData.pushNavigationInteraction(
      NavigationEventType.MainMenu,
      'services',
      mobile.isSolutionsOpen ? 'open_menu' : 'close_menu'
    )
  }, [mobile.isSolutionsOpen])
  const { data } = useGraphql<GetCategoryNavigationQuery>(GET_CATEGORY_NAVIGATION, {})

  const allCategories = data?.categoryNavigation?.children || []

  const menuHeight =
    menuRef.current && window && window.innerHeight - menuRef.current.getBoundingClientRect().top

  const showFooter = !mobile.breadcrumbs

  const onCategoryClick = (id: string) => {
    setActiveCategory(id)
    dispatchMobile({
      type: MobileStateActionType.SET_BREADCRUMBS,
      payload: {
        breadcrumbs: [
          ...(mobile.breadcrumbs ?? []),
          {
            categoryId: id,
            action: () => {
              setActiveCategory(TOP_CATEGORY_ID)
              setAccordionsRefreshKey(prevKey => prevKey + 1)
            },
          },
        ],
      },
    })
  }

  const onBrandsClick = () => {
    setShowMobileBrands(true)

    dispatchMobile({
      type: MobileStateActionType.SET_BREADCRUMBS,
      payload: {
        breadcrumbs: [
          ...(mobile.breadcrumbs ?? []),
          {
            action: () => setShowMobileBrands(false),
          },
        ],
      },
    })
  }

  const onSolutionsClick = () => {
    setShowMobileSolutions(true)
    dispatchMobile({
      type: MobileStateActionType.SET_ACTIVE_SUB_MENU,
      payload: { subMenu: SubMenu.SOLUTIONS },
    })

    dispatchMobile({
      type: MobileStateActionType.SET_BREADCRUMBS,
      payload: {
        breadcrumbs: [
          {
            action: () => {
              dispatchMobile({
                type: MobileStateActionType.SET_ACTIVE_SUB_MENU,
                payload: { subMenu: null },
              })
              setShowMobileSolutions(false)
            },
          },
        ],
      },
    })
  }

  return (
    <div ref={menuRef} style={{ display: 'flex' }}>
      <div
        className={classNames('c-mobile-header-menu ', {
          'c-mobile-header-menu--active': mobile.showMenu,
        })}
        style={{ height: menuHeight ? `${menuHeight}px` : 'auto' }}>
        <MobileHeaderBreadcrumb />
        <ScrollContainer
          className={classNames('u-flex-1 u-h-100 u-overflow-hidden t-gray-09')}
          small
          height="100%">
          <ul
            className={classNames('c-mobile-header-menu__primary-list ul-stripped', {
              'c-mobile-header-menu__primary-list--dismissed':
                activeCategory !== TOP_CATEGORY_ID || showMobileBrands || mobile.isSolutionsOpen,
            })}>
            <li>
              <a
                className="u-py-8 u-decoration-none u-fs-20 u-w-100 d-flex align-items-center"
                data-ga-title="Show_Bargains"
                href={translate('Bargains_URL')}
                onClick={() =>
                  DigitalData.pushNavigationInteraction(
                    NavigationEventType.MainMenu,
                    'bargains',
                    'link_click'
                  )
                }>
                {getMainCategoryIcon('bargain', 'i-16 u-mr-16')}
                {translate('Show_Bargains')}
              </a>
            </li>
            <li>
              <a
                className="u-py-8 u-decoration-none u-fs-20 u-w-100 d-flex align-items-center"
                data-ga-title="campaigns"
                href={stripTrailingSlash(translate('Campaigns_URL'))}
                onClick={() =>
                  DigitalData.pushNavigationInteraction(
                    NavigationEventType.MainMenu,
                    'campaigns',
                    'link_click'
                  )
                }>
                {getMainCategoryIcon('label', 'i-16 u-mr-16')}
                {translate('Show_Campaigns')}
              </a>
            </li>
            <li>
              <a
                className="u-py-8 u-decoration-none u-fs-20 u-w-100 d-flex align-items-center"
                data-ga-title="news"
                href={translate('New_Products_URL')}
                onClick={() =>
                  DigitalData.pushNavigationInteraction(
                    NavigationEventType.MainMenu,
                    'news',
                    'link_click'
                  )
                }>
                {getMainCategoryIcon('idea', 'i-16 u-mr-16')}
                {translate('Header_News')}
              </a>
            </li>
            {allCategories && (
              <>
                <li className="c-mobile-header-menu__primary-list-devider-mob" />
                {allCategories.map((child: any, key: number) => (
                  <CategoryListButton
                    key={child.categoryId + key}
                    icon={child.categoryId}
                    text={child.categoryDisplayName}
                    gaTitle={child.categoryDisplayName}
                    onClick={() => {
                      onCategoryClick(child.categoryId)
                      DigitalData.pushNavigationInteraction(
                        NavigationEventType.MainMenu,
                        child.categoryEnglishDisplayName,
                        'dropdown_open',
                        { selectionType: getCategoryType(child.categoryId) }
                      )
                    }}
                  />
                ))}
                <li className="c-mobile-header-menu__primary-list-devider-mob" />
              </>
            )}
            <li>
              <button
                data-ga-title="brands"
                className="c-mobile-header-menu__primary-action c-mobile-header-menu__primary-action--w-sub-menu u-fs-16"
                onClick={onBrandsClick}>
                {translate('Header_Brands')}
              </button>
            </li>
            <li>
              <a
                data-ga-title="accessory-guide"
                href="/accessories/guides"
                className="c-mobile-header-menu__primary-action u-fs-16">
                {translate('Header_AccessoryFinder')}
              </a>
            </li>
            <li>
              <button
                data-ga-title="solutions"
                className="c-mobile-header-menu__primary-action c-mobile-header-menu__primary-action--w-sub-menu u-fs-16"
                onClick={() => onSolutionsClick()}>
                {translate('Header_Solutions')}
              </button>
            </li>
            <li>
              <a
                data-ga-title="knowledge-bank"
                href={getKnowledgeBankUrl(sourcingCompany)}
                className="c-mobile-header-menu__primary-action u-fs-16">
                {translate('Header_KnowledgeBank')}
              </a>
            </li>
            <li>
              <a
                data-ga-title="customer-service"
                href={translate('CustomerService_URL')}
                className="c-mobile-header-menu__primary-action u-fs-16">
                {translate('Header_CustomerServiceSupport')}
              </a>
            </li>
          </ul>
          <CategorySecondLevel
            allCategories={allCategories}
            activeCategory={activeCategory}
            accordionsRefreshKey={accordionsRefreshKey}
          />
          <>
            <BrandMenu showMobileBrands={showMobileBrands} />
            <SolutionsMenu showMobileSolutions={showMobileSolutions} />
          </>
        </ScrollContainer>
        {showFooter && (
          <div className="c-mobile-header-menu__footer">
            <SiteLangSwitcher />
          </div>
        )}
      </div>
    </div>
  )
}

export default MobileHeaderMenuABTest
